{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "title": "FieldColourMapFile",
  "description": "The colour Cornelis Anthonisz. painted the polders in, on a ground grid: every painting pixel the painter view draws on a polder and paints as a field, its painter-view haze undone, spread over the ground by a Gaussian, with a weight for how much of each cell's neighbourhood the painting shows. Written by tools/fields from docs/reference/cornelis-anthonisz-1538.jpg seen through the painter camera (src/data/painterView.json).",
  "type": "object",
  "additionalProperties": false,
  "required": ["version", "source", "method", "grid", "albedo", "weight", "far"],
  "properties": {
    "version": {
      "description": "Schema version.",
      "const": 1
    },
    "source": {
      "title": "FieldColourMapSource",
      "type": "object",
      "additionalProperties": false,
      "required": ["image", "width", "height"],
      "properties": {
        "image": {
          "type": "string",
          "description": "Project-relative path of the painting."
        },
        "width": {
          "type": "integer",
          "description": "Pixels."
        },
        "height": {
          "type": "integer",
          "description": "Pixels."
        }
      }
    },
    "method": {
      "title": "FieldColourMapMethod",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "blurSigmaPx",
        "stepPx",
        "spreadSigmaM",
        "fullWeightCoverage",
        "looks",
        "lastageLooks"
      ],
      "properties": {
        "blurSigmaPx": {
          "type": "number",
          "description": "Standard deviation of the Gaussian blur applied to the painting before sampling, pixels."
        },
        "stepPx": {
          "type": "integer",
          "minimum": 1,
          "description": "Every stepPx-th pixel in each direction is sampled."
        },
        "spreadSigmaM": {
          "type": "number",
          "exclusiveMinimum": 0,
          "description": "Standard deviation of the Gaussian that spreads the samples over the ground, metres."
        },
        "fullWeightCoverage": {
          "type": "number",
          "exclusiveMinimum": 0,
          "maximum": 1,
          "description": "Share of a cell's Gaussian neighbourhood the painting must show for the cell to take the painted colour whole; below it the weight falls in proportion."
        },
        "looks": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "minItems": 1,
          "description": "Palette classes a sampled pixel must be nearest to: the looks of a field."
        },
        "lastageLooks": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "minItems": 1,
          "description": "Palette classes a sampled pixel on the Lastage's ground (its own ground and the gardens and yards in it) must be nearest to: every look but sky, so the painter's dark earth and the ditches of his meadows there come in with his gardens."
        }
      }
    },
    "grid": {
      "$ref": "#/$defs/grid"
    },
    "albedo": {
      "type": "string",
      "pattern": "^[A-Za-z0-9+/]*={0,2}$",
      "description": "Base64 of nx * ny * 3 bytes: each cell's albedo as sRGB bytes, rows from the south, cells from the west."
    },
    "weight": {
      "type": "string",
      "pattern": "^[A-Za-z0-9+/]*={0,2}$",
      "description": "Base64 of nx * ny bytes: each cell's weight, 0 to 255 for none to whole, in the order of albedo."
    },
    "far": {
      "title": "FieldColourMapFarLayer",
      "description": "The painter's ground past the layout bounds, where the far ring continues the polders: the same survey on a coarser grid of its own, since there a painting pixel spans hundreds of metres of ground. The painted material takes it where the fine grid has no colour.",
      "type": "object",
      "additionalProperties": false,
      "required": ["spreadSigmaM", "fullWeightCoverage", "grid", "albedo", "weight"],
      "properties": {
        "spreadSigmaM": {
          "type": "number",
          "exclusiveMinimum": 0,
          "description": "Standard deviation of the Gaussian that spreads the samples over the ground, metres."
        },
        "fullWeightCoverage": {
          "type": "number",
          "exclusiveMinimum": 0,
          "maximum": 1,
          "description": "Share of a cell's Gaussian neighbourhood the painting must show for the cell to take the painted colour whole; below it the weight falls in proportion."
        },
        "grid": {
          "$ref": "#/$defs/grid"
        },
        "albedo": {
          "type": "string",
          "pattern": "^[A-Za-z0-9+/]*={0,2}$",
          "description": "Base64 of nx * ny * 3 bytes: each cell's albedo as sRGB bytes, rows from the south, cells from the west."
        },
        "weight": {
          "type": "string",
          "pattern": "^[A-Za-z0-9+/]*={0,2}$",
          "description": "Base64 of nx * ny bytes: each cell's weight, 0 to 255 for none to whole, in the order of albedo."
        }
      }
    }
  },
  "$defs": {
    "grid": {
      "title": "FieldColourMapGrid",
      "type": "object",
      "additionalProperties": false,
      "required": ["x0", "y0", "cellM", "nx", "ny"],
      "properties": {
        "x0": {
          "type": "number",
          "description": "Layout x (east) of the grid's west edge, metres."
        },
        "y0": {
          "type": "number",
          "description": "Layout y (north) of the grid's south edge, metres."
        },
        "cellM": {
          "type": "number",
          "exclusiveMinimum": 0,
          "description": "Side of a cell, metres."
        },
        "nx": {
          "type": "integer",
          "minimum": 1,
          "description": "Cells from west to east."
        },
        "ny": {
          "type": "integer",
          "minimum": 1,
          "description": "Cells from south to north."
        }
      }
    }
  }
}
